import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import { SectionGroup } from '@/types/Section'
import StaffRateRow from './staffRateTableRow'
import styles from './staffRate.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Desktop from '@/modules/desktopMobile/desktop'
import StaffRateForm from './Form/staffRateForm'
import { useAuth } from '@/modules/context/AuthContext'
import { useQuery } from '@apollo/client'
import { GET_SECTION_GROUPS } from '@/modules/API/staff/staffRate'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface StaffRateProps {
    daycare: Daycare
    canUpdate: boolean
}

const StaffRate: FunctionComponent<StaffRateProps> = ({ daycare, canUpdate }: StaffRateProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const [staffRateList, setStaffRateList] = useState<Array<SectionGroup>>([])

    const { dialog: manageStaffRateDialog, openDialog: openManageStaffRate } = useDialog({
        dialogTitle: t('addStaffRate'),
        Component: StaffRateForm,
        props: (onClose) => ({
            handleClose: () => {
                onClose()
                refetch()
            },
            create: true,
            refetchStaffRates: refetch,
            daycare
        }),
        hideDialogClose: true
    })

    const {
        loading: loadingSectionGroups,
        error: errorSectionGroups,
        data: dataSectionGroups,
        refetch: refetchSectionGroups
    } = useQuery<{
        getSectionGroups: Array<SectionGroup>
    }>(GET_SECTION_GROUPS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (dataSectionGroups) {
            setStaffRateList(
                [...dataSectionGroups.getSectionGroups].sort((a: SectionGroup, b: SectionGroup) =>
                    a.name.localeCompare(b.name)
                )
            )
        }
    }, [dataSectionGroups])

    function refetch() {
        refetchSectionGroups()
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.StaffRatePlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myStaffRate')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon type={'add'} className={styles.Button} onClick={openManageStaffRate} />
                    )}
                </div>
                {loadingSectionGroups && <Loading overall={false} />}
                {dataSectionGroups && daycare.staffAccess && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell sx={{ width: '40%' }}>{t('name')}</TableCell>
                                        <Desktop>
                                            <TableCell sx={{ width: '25%' }}>{t('childsPerStaff')}</TableCell>
                                            <TableCell sx={{ width: '25%' }}>{t('sectionsAssociated')}</TableCell>
                                        </Desktop>
                                        {canUpdate && (
                                            <TableCell colSpan={2} sx={{ width: '10%' }}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staffRateList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>{t('noStaffRate')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        staffRateList.map((sectionGroup: SectionGroup, index: number) => (
                                            <StaffRateRow
                                                key={
                                                    sectionGroup.id +
                                                    sectionGroup.rate +
                                                    sectionGroup.name +
                                                    sectionGroup.sections +
                                                    index
                                                }
                                                sectionGroup={sectionGroup}
                                                daycare={daycare}
                                                canUpdate={canUpdate}
                                                refetchStaffRates={refetch}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {manageStaffRateDialog}
                    </>
                )}
                {errorSectionGroups && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default StaffRate
